import { appApi, idTag, orgTag } from '@/app/redux/appApi';
import { Group } from '@/common/types/group';
import api from '@/db/apiClient';

const groupsApi = appApi.injectEndpoints({
  endpoints: (build) => ({
    getGroupsForOrganisation: build.query<Group[], string>({
      query: (orgId) =>
        api.asEducator.getGroupsForOrganisation(orgId),
      providesTags: (result, _err, orgId) =>
        result
          ? [orgTag('Groups', orgId), ...result.map((g) => idTag('Groups', g.id))]
          : [orgTag('Groups', orgId)],
    }),

    createGroup: build.mutation<Group, Omit<Group, 'id'>>({
      query: async (data) => {
        const id = await api.asEducator.createGroup(data);
        return { id, ...data };
      },
      invalidatesTags: (_res, _err, data) => [orgTag('Groups', data.organisationId)],
    }),

    updateGroup: build.mutation<
      null,
      { orgId: string; groupId: string; updates: Partial<Group> }
    >({
      query: async ({ groupId, updates }) => {
        await api.asEducator.updateGroup(groupId, updates);
        return null;
      },
      onQueryStarted: async (
        { orgId, groupId, updates },
        { dispatch, queryFulfilled },
      ) => {
        const patch = dispatch(
          groupsApi.util.updateQueryData(
            'getGroupsForOrganisation',
            orgId,
            (draft) => {
              const g = draft.find((x) => x.id === groupId);
              if (g) Object.assign(g, updates, { updatedAt: Date.now() });
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),

    deleteGroup: build.mutation<null, { orgId: string; groupId: string }>({
      query: async ({ groupId }) => {
        await api.asEducator.deleteGroup(groupId);
        return null;
      },
      onQueryStarted: async (
        { orgId, groupId },
        { dispatch, queryFulfilled },
      ) => {
        const patch = dispatch(
          groupsApi.util.updateQueryData(
            'getGroupsForOrganisation',
            orgId,
            (draft) => {
              const idx = draft.findIndex((g) => g.id === groupId);
              if (idx !== -1) draft.splice(idx, 1);
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),
  }),

  overrideExisting: false,
});

export default groupsApi;
export const {
  useGetGroupsForOrganisationQuery,
  useCreateGroupMutation,
  useUpdateGroupMutation,
  useDeleteGroupMutation,
} = groupsApi;
